import { ActionTree, ActionContext } from 'vuex';
import { RecordModuleState } from './types';
import { RootState } from '@/store/types';
import { ScanInput, PutItemInput, GetItemInput, DeleteItemInput } from 'aws-sdk/clients/dynamodb';
import { INITIAL_LIMIT } from './mutations';
import { buildFilterExpression, buildLegacyFilter } from './filterExpression';

/* Format to create 50 rows */
// let Item;
// try {
//   for (let i = 0; i < 50; i++) {
//     const Item = {
//       id: Math.random() + Math.random() + '',
//       [Math.random() + '']: Math.random() + Math.random() + '',
//     };
//     const params: any = {
//       TableName: currentTable,
//       Item,
//     };
//     await dbClient.put(params).promise();
//   }
// }
async function putItem({
  dispatch,
  commit,
  rootState,
  state,
}: ActionContext<RecordModuleState, RootState>) {
  if (!(await dispatch('ensureCurrentDb', null, { root: true }))) {
    return;
  }
  const { dbClient } = rootState;
  const { currentTable } = rootState;
  const Item = state.recordMeta;
  const params: PutItemInput = {
    TableName: currentTable,
    Item,
  };
  try {
    await dbClient.put(params).promise();
  } catch (err) {
    commit('showResponse', err, { root: true });
    return;
  }
  commit('addItemToList', Item);
  commit('setHeader');
  commit('toggleCreateModal');
  commit('showResponse', ' ', { root: true });
  dispatch('table/getMeta', null, { root: true });
}

async function getItem(
  { state, commit, rootState, dispatch }: ActionContext<RecordModuleState, RootState>,
  row: any,
) {
  if (!(await dispatch('ensureCurrentDb', null, { root: true }))) {
    return;
  }
  const { dbClient } = rootState;
  const { currentTable } = rootState;
  const params: GetItemInput = {
    TableName: currentTable,
    Key: {
      [state.hashKey]: row[state.hashKey],
      [state.rangeKey]: row[state.rangeKey],
    },
  };
  let data;
  try {
    data = await dbClient.get(params).promise();
  } catch (err) {
    commit('showResponse', err, { root: true });
    return;
  }
  commit('setMeta', data.Item);
}
async function removeItem({
  commit,
  rootState,
  dispatch,
  state,
}: ActionContext<RecordModuleState, RootState>) {
  if (!(await dispatch('ensureCurrentDb', null, { root: true }))) {
    return;
  }
  const { dbClient } = rootState;
  const { currentTable } = rootState;
  const row = state.recordMeta;
  const Key = {
    [state.hashKey]: row[state.hashKey],
    [state.rangeKey]: row[state.rangeKey],
  };
  const params: DeleteItemInput = {
    TableName: currentTable,
    Key,
  };
  try {
    await dbClient.delete(params).promise();
  } catch (err) {
    commit('showResponse', err, { root: true });
    return;
  }
  commit('deleteItemFromList', Key);
  commit('toggleDeleteModal');
  commit('setHeader');
  commit('showResponse', ' ', { root: true });
  dispatch('table/getMeta', null, { root: true });
}

async function removeSelected({
  commit,
  rootState,
  dispatch,
  state,
}: ActionContext<RecordModuleState, RootState>) {
  if (!(await dispatch('ensureCurrentDb', null, { root: true }))) {
    return;
  }
  const { dbClient } = rootState;
  const { currentTable } = rootState;
  let result = false;
  /*
    When selected 26 or more items, we need to send multiple request due
    to AWS batchWrite limitation
  */
  state.groupDelete.forEach(async (group: any) => {
    const params = {
      RequestItems: {
        [currentTable]: group,
      },
    };
    let response: any;
    try {
      response = await dbClient.batchWrite(params).promise();
    } catch (err) {
      result = false;
      commit('showResponse', err, { root: true });
      return;
    }
    // If there was unprocessed items, retry deleting.
    if (state.retry < 5 && response.UnprocessedItems.currentTable) {
      commit('setGroupDeleteItems', response.UnprocessedItems.currentTable);
      dispatch('removeSelected');
    }
    state.selectedRows.forEach((item) => {
      commit('deleteItemFromList', item);
    });
    state.showGroupDeleteModal && commit('toggleGroupDeleteModal');
    commit('setHeader');
    dispatch('table/getMeta', null, { root: true });
    result = true;
  });
  result && commit('showResponse', ' ', { root: true });
}

function generateMeta({
  commit,
  state,
}: ActionContext<RecordModuleState, RootState>) {
  const { hashKey, rangeKey } = state;
  let meta;
  rangeKey
    ? (meta = {
        [hashKey]: '',
        [rangeKey]: '',
      })
    : (meta = {
        [hashKey]: '',
      });
  commit('setMeta', meta);
  commit('toggleCreateModal');
}

// tslint:disable-next-line:max-line-length
async function getRecords(
  {
    dispatch,
    commit,
    rootState,
    state,
  }: ActionContext<RecordModuleState, RootState>,
  params: ScanInput,
) {
  if (!(await dispatch('ensureCurrentDb', null, { root: true }))) {
    return;
  }
  const { dbClient } = rootState;
  const { currentTable } = rootState;
  const isClientPagination = state.paginationMode === 'client';
  !isClientPagination && commit('loading', true, { root: true });
  let filterResult: any = {};
  if (state.filtered) {
    if (state.useAdvancedFilter) {
      filterResult = buildFilterExpression(
        state.advancedFilter.conditions,
        state.advancedFilter.logicalOperator,
      ) || {};
    } else {
      filterResult = buildLegacyFilter(
        state.filterParams.filterColumn,
        state.filterParams.filterExpr,
        state.filterParams.filterValue,
      );
    }
  }

  commit('startScan');
  const myGeneration = state.scanGeneration;

  if (isClientPagination) {
    commit('setData', []);
  }

  let lastEvaluatedKey = state.evaluatedKeys[state.lastEvaluatedKeyIndex - 1];
  let firstPage = true;

  try {
    while (true) {
      if (state.scanGeneration !== myGeneration) {
        if (isClientPagination) { commit('flushScanBuffer'); }
        !state.scanning && commit('loading', false, { root: true });
        return;
      }

      const result = await dbClient.scan({
        TableName: currentTable,
        Limit: isClientPagination ? undefined : state.limit,
        ExclusiveStartKey: lastEvaluatedKey,
        ...(state.filtered ? filterResult : {}),
        ...params,
      }).promise();

      if (state.scanGeneration !== myGeneration) {
        if (isClientPagination) { commit('flushScanBuffer'); }
        !state.scanning && commit('loading', false, { root: true });
        return;
      }

      if (isClientPagination) {
        if ((result.Items || []).length) {
          commit('appendData', result.Items);
        }
        if (!result.LastEvaluatedKey) { break; }
        lastEvaluatedKey = result.LastEvaluatedKey;
        // Yield to the UI so it can paint and handle clicks (e.g. Cancel)
        await new Promise((r) => requestAnimationFrame(r));
      } else {
        if (firstPage && !(result.Items || []).length && result.LastEvaluatedKey) {
          lastEvaluatedKey = result.LastEvaluatedKey;
          firstPage = false;
          continue;
        }
        commit('setData', result.Items);
        commit('setHeader');
        result.LastEvaluatedKey && commit('addEvaluatedKey', result.LastEvaluatedKey);
        break;
      }
    }
  } catch (err) {
    if (isClientPagination) { commit('flushScanBuffer'); }
    commit('showResponse', err, { root: true });
    commit('loading', false, { root: true });
    commit('finishScan');
    return;
  }
  if (isClientPagination) { commit('flushScanBuffer'); }
  commit('finishScan');
  commit('loading', false, { root: true });
  dispatch('table/getMeta', null, { root: true });
}

async function filterRecords({
  dispatch,
  getters,
  commit,
}: ActionContext<RecordModuleState, RootState>) {
  if (!getters.scanIsValid) {
    commit(
      'showResponse',
      { message: 'Please fill all scan fields.' },
      { root: true },
    );
    return;
  }
  commit('clearEvaluatedKeys');
  commit('changeFilterValueType');
  commit('setFilterStatus');
  dispatch('getRecords');
}
async function filterAdvancedRecords({
  dispatch,
  getters,
  commit,
}: ActionContext<RecordModuleState, RootState>) {
  if (!getters.advancedScanIsValid) {
    commit(
      'showResponse',
      { message: 'Please fill all scan fields in every condition.' },
      { root: true },
    );
    return;
  }
  commit('clearEvaluatedKeys');
  commit('setUseAdvancedFilter', true);
  commit('setFilterStatus');
  dispatch('getRecords');
}

async function getLimitedRows(
  { state, commit, dispatch }: ActionContext<RecordModuleState, RootState>,
  limit: any,
) {
  if (isNaN(limit)) {
    commit(
      'showResponse',
      { message: 'Limit must be a number' },
      { root: true },
    );
  } else {
    commit('setLimit', (typeof limit === 'number' && limit <= 0) ? INITIAL_LIMIT : limit);
    if (state.paginationMode === 'client' && state.scanRowCount > 0) {
      commit('flushScanBuffer');
    } else {
      commit('clearEvaluatedKeys');
      dispatch('getRecords');
    }
  }
}

async function getPreviousRecords({
  state,
  commit,
  dispatch,
}: ActionContext<RecordModuleState, RootState>) {
  commit('lastEvaluatedKeyIndexDec');
  dispatch('getRecords');
}

async function getNextRecords({
  state,
  commit,
  dispatch,
}: ActionContext<RecordModuleState, RootState>) {
  commit('lastEvaluatedKeyIndexInc');
  dispatch('getRecords');
}

async function refreshTable({
  commit,
  dispatch,
}: ActionContext<RecordModuleState, RootState>) {
  commit('initialState');
  dispatch('getRecords');
}

const actions: ActionTree<RecordModuleState, RootState> = {
  getRecords,
  putItem,
  getItem,
  removeItem,
  removeSelected,
  generateMeta,
  getLimitedRows,
  getPreviousRecords,
  getNextRecords,
  refreshTable,
  filterRecords,
  filterAdvancedRecords,
};

export default actions;
